import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table"
import { Checkbox } from "../ui/checkbox"
import { Button } from "../ui/button"
import { Trash2, Pencil, ArrowUpDown, ArrowDown, ArrowUp } from 'lucide-react'; // Added sort icons
import apiClient from '../../services/api'; // Import API client
import { Task, Category } from '../../types'; // Import Task and Category types
import { Badge } from '../ui/badge'; // Import Badge

// Define SortField and SortDirection types here or import them
type SortField = 'task_date' | 'category__name' | 'completed' | 'one_thing';
type SortDirection = 'asc' | 'desc';

interface TaskListProps {
  tasks: Task[];
  categories: Category[]; // Add categories prop
  refreshTasks: () => void; // Function to refresh task list
  onEditTask: (task: Task) => void; // Add prop for edit handler
  // Add sorting props
  sortField: SortField;
  sortDirection: SortDirection;
  onSortChange: (field: SortField) => void;
}

const TaskList: React.FC<TaskListProps> = ({ 
  tasks, 
  categories, 
  refreshTasks, 
  onEditTask, 
  // Destructure sorting props
  sortField,
  sortDirection,
  onSortChange
}) => {
  const [loadingTaskId, setLoadingTaskId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTaskCompletionToggle = async (task: Task) => {
    setLoadingTaskId(task.id);
    setError(null);
    try {
      await apiClient.patch(`/goals/tasks/${task.id}/`, { completed: !task.completed });
      refreshTasks(); // Refresh the list to show updated status and potentially UserStats
    } catch (err: any) {
      console.error("Failed to update task status:", err);
      setError(`Failed to update task ${task.id}.`); // Basic error handling
    } finally {
      setLoadingTaskId(null);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    setLoadingTaskId(taskId); // Show loading state on delete button maybe?
    setError(null);
    try {
      await apiClient.delete(`/goals/tasks/${taskId}/`);
      refreshTasks();
    } catch (err: any) {
      console.error("Failed to delete task:", err);
      setError(`Failed to delete task ${taskId}.`);
    } finally {
      setLoadingTaskId(null);
    }
  }

  // Helper to find category name
  const getCategoryName = (categoryId: number | null) => {
    if (!categoryId) return null;
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : null;
  };
  
  // Helper component for sortable headers
  const SortableHeader: React.FC<{ field: SortField; label: string; className?: string }> = ({ field, label, className }) => {
    const isSorting = sortField === field;
    const Icon = isSorting ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <TableHead className={className}>
        <Button variant="ghost" onClick={() => onSortChange(field)} className="px-2 py-1 h-auto -ml-2">
          {label}
          <Icon className={`ml-2 h-3 w-3 ${isSorting ? 'text-primary' : 'text-muted-foreground/70'}`} />
        </Button>
      </TableHead>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tasks</CardTitle>
        <CardDescription>Your tasks for selected goals/dates.</CardDescription>
        {error && <p className="text-sm text-red-500 mt-2">Error: {error}</p>}
      </CardHeader>
      <CardContent>
        <Table>
          <TableCaption>A list of your tasks.</TableCaption>
          <TableHeader>
            <TableRow>
              {/* Use SortableHeader for columns */} 
              <SortableHeader field="completed" label="Done" className="w-[80px]" />
              <TableHead>Title</TableHead> {/* Title not sortable for now */}
              <SortableHeader field="category__name" label="Category" />
              <TableHead>Goal</TableHead>
              <SortableHeader field="task_date" label="Date" />
              <SortableHeader field="one_thing" label="ONE THING" />
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center">No tasks found.</TableCell>
              </TableRow>
            )}
            {tasks.map((task) => {
              const categoryName = getCategoryName(task.category);
              return (
                <TableRow key={task.id}>
                  <TableCell>
                    <Checkbox 
                      checked={task.completed}
                      onCheckedChange={() => handleTaskCompletionToggle(task)}
                      disabled={loadingTaskId === task.id}
                      aria-label={`Mark task ${task.title} as ${task.completed ? 'incomplete' : 'complete'}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{task.title}</TableCell>
                  <TableCell>
                    {categoryName && <Badge variant="outline">{categoryName}</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]">
                    {task.goal_details?.title}
                  </TableCell>
                  <TableCell>{task.task_date}</TableCell>
                  <TableCell>{task.one_thing ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => onEditTask(task)}
                      disabled={loadingTaskId === task.id}
                      aria-label={`Edit task ${task.title}`}
                    >
                      <Pencil className="h-4 w-4" /> 
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteTask(task.id)}
                      disabled={loadingTaskId === task.id}
                      aria-label={`Delete task ${task.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default TaskList; 